import { TimePicker } from "@/components/AntdMomentPicker";
import { Form, Space } from "antd";
import React from "react";
import { Select } from "../../BaseSelect";
import TimeType from "@/scenes/OrderManagement/enum/TimeType";
import { Moment } from "moment";
import moment from "moment";
import { isEmpty } from "@/utils";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";

interface TimeTypeFormFieldProps {}

enum DayOffset {
    SAME_DAY = 0,
    ONE_DAY_BEFORE = -1,
    TWO_DAY_BEFORE = -2,
    THREE_DAY_BEFORE = -3,
    FOR_DAY_BEFORE = -4,
    FIVE_DAY_BEFORE = -5,
}

const DayOffsetNameMap = {
    [DayOffset.SAME_DAY]: "当天",
    [DayOffset.ONE_DAY_BEFORE]: "前1天",
    [DayOffset.TWO_DAY_BEFORE]: "前2天",
    [DayOffset.THREE_DAY_BEFORE]: "前3天",
    [DayOffset.FOR_DAY_BEFORE]: "前4天",
    [DayOffset.FIVE_DAY_BEFORE]: "前5天",
};

const TimeTypeFormField: React.FC<TimeTypeFormFieldProps> = function () {
    const dayOffsetOptions = Object.keys(DayOffsetNameMap).map((step) => ({
        label: DayOffsetNameMap[step],
        value: Number(step),
    }));

    const getDateTime = (offset?: number, time?: Moment | null) => {
        if (isEmpty(offset) || !time) return null;
        const base = moment().startOf("day");
        return base.clone().add(offset, "day").set({
            hour: time.hour(),
            minute: time.minute(),
            second: time.second(),
        });
    };

    const parseTimeString = (timeStr?: string): Moment | null => {
        return timeStr ? moment(timeStr, "HH:mm:ss") : null;
    };

    return (
        <Form.Item<TableSearchState> noStyle shouldUpdate>
            {(form) => {
                const { timeTo, timeFrom, timeFromOffset, timeToOffset, timeType } = form.getFieldsValue();

                const handleFromChange = (offset?: number, time?: Moment | null) => {
                    if (isEmpty(offset)) {
                        form.resetFields(["timeFrom"]);
                    }

                    const start = getDateTime(offset ?? timeFromOffset, time ?? parseTimeString(timeFrom));
                    const end = getDateTime(timeToOffset, parseTimeString(timeTo));
                    if (start && end && start.isAfter(end)) {
                        form.setFieldsValue({ timeToOffset: undefined, timeTo: undefined });
                    }
                };

                const handleToChange = (offset?: number, time?: Moment | null) => {
                    if (isEmpty(offset)) {
                        form.resetFields(["timeTo"]);
                    }
                    const start = getDateTime(timeFromOffset, parseTimeString(timeFrom));
                    const end = getDateTime(offset ?? timeToOffset, time ?? parseTimeString(timeTo));
                    if (start && end && end.isBefore(start)) {
                        form.setFieldsValue({ timeFromOffset: undefined, timeFrom: undefined });
                    }
                };

                const handleTimeTypeChange = (type) => {
                    if (!type) {
                        form.setFieldsValue({
                            timeToOffset: undefined,
                            timeTo: undefined,
                            timeFromOffset: undefined,
                            timeFrom: undefined,
                        });
                    }
                };

                return (
                    <Form.Item
                        label={<span className="search-field-label">时间范围</span>}
                        className="time-type-field-container"
                    >
                        <div style={{ display: "flex" }}>
                            {/* 时间类型 */}
                            <Form.Item name="timeType" noStyle>
                                <Select
                                    style={{ width: 110 }}
                                    allowClear
                                    options={[
                                        { label: "下单时间", value: TimeType.ORDER },
                                        { label: "付款时间", value: TimeType.PAY },
                                        { label: "打印时间", value: TimeType.PRINT },
                                        { label: "发货时间", value: TimeType.SHIP },
                                    ]}
                                    onChange={handleTimeTypeChange}
                                />
                            </Form.Item>

                            <div style={{ display: "flex", alignItems: "center" }}>
                                {/* 开始 */}
                                <Space.Compact className="ml25">
                                    <Form.Item name="timeFromOffset" noStyle>
                                        <Select
                                            disabled={!timeType}
                                            placeholder="起始"
                                            style={{ width: 90 }}
                                            options={dayOffsetOptions}
                                            allowClear
                                            onChange={(val) => handleFromChange(val, parseTimeString(timeFrom))}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="timeFrom"
                                        noStyle
                                        getValueFromEvent={(val: Moment | null) =>
                                            val ? val.format("HH:mm:ss") : undefined
                                        }
                                        getValueProps={(val: string) => ({
                                            value: val ? moment(val, "HH:mm:ss") : null,
                                        })}
                                    >
                                        <TimePicker
                                            disabled={isEmpty(timeFromOffset)}
                                            size="small"
                                            format="HH:mm:ss"
                                            showNow={false}
                                            style={{ width: 100 }}
                                            suffixIcon={null}
                                            allowClear={false}
                                            onChange={(val) => handleFromChange(timeFromOffset, val)}
                                        />
                                    </Form.Item>
                                </Space.Compact>

                                <span className="ml10">到</span>

                                {/* 结束 */}
                                <Space.Compact className="ml10">
                                    <Form.Item name="timeToOffset" noStyle>
                                        <Select
                                            disabled={!timeType}
                                            placeholder="结束"
                                            style={{ width: 90 }}
                                            options={dayOffsetOptions}
                                            allowClear
                                            onChange={(val) => handleToChange(val, parseTimeString(timeTo))}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="timeTo"
                                        noStyle
                                        getValueFromEvent={(val: Moment | null) =>
                                            val ? val.format("HH:mm:ss") : undefined
                                        }
                                        getValueProps={(val: string) => ({
                                            value: val ? moment(val, "HH:mm:ss") : null,
                                        })}
                                    >
                                        <TimePicker
                                            disabled={isEmpty(timeToOffset)}
                                            size="small"
                                            format="HH:mm:ss"
                                            showNow={false}
                                            style={{ width: 100 }}
                                            suffixIcon={null}
                                            allowClear={false}
                                            onChange={(val) => handleToChange(timeToOffset, val)}
                                        />
                                    </Form.Item>
                                </Space.Compact>
                            </div>
                        </div>
                    </Form.Item>
                );
            }}
        </Form.Item>
    );
};

export default React.memo(TimeTypeFormField);
